import _ from 'lodash';
import moment from 'moment';

export const sessionCardStates = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

// A member counts as done once their own status reaches PARTIAL_COMPLETE:
// the group status only turns COMPLETED when every member is done.
const doneStatuses = ['PARTIAL_COMPLETE', 'COMPLETED'];

const isDone = status => _.includes(doneStatuses, status);

export const partnersOf = sessionStatus =>
  _.get(sessionStatus, 'partnerCompletedSteps') || [];

export const deadlineState = (endDate, now = moment()) => {
  const hasDeadline = Boolean(endDate) && moment(endDate).isValid();
  const daysRemaining = hasDeadline
    ? moment(endDate).startOf('day').diff(moment(now).startOf('day'), 'days')
    : null;
  const isOverdue = hasDeadline && daysRemaining < 0;

  return {
    hasDeadline,
    daysRemaining,
    isOverdue,
    daysOverdue: isOverdue ? Math.abs(daysRemaining) : 0,
  };
};

export const curriculumSessionState = (sessionStatus = {}, now = moment()) => {
  const partners = partnersOf(sessionStatus);
  const myStatus = sessionStatus.status;
  const iAmDone = isDone(myStatus);

  const donePartners = _.filter(partners, p => isDone(p.partnerStatus));
  const pendingPartnerCount = partners.length - donePartners.length;
  const anyoneStarted =
    myStatus !== 'NOT_STARTED' ||
    _.some(partners, p => p.partnerStatus !== 'NOT_STARTED');

  const {hasDeadline, daysRemaining, isOverdue, daysOverdue} = deadlineState(
    sessionStatus.endDate,
    now,
  );

  let card = sessionCardStates.IN_PROGRESS;
  if (myStatus === 'COMPLETED') {
    card = sessionCardStates.COMPLETED;
  } else if (!anyoneStarted) {
    card = sessionCardStates.NOT_STARTED;
  }

  const completedMemberCount = donePartners.length + (iAmDone ? 1 : 0);
  const totalMemberCount = partners.length + 1;

  return {
    card,
    iAmDone,
    isOverdue,
    hasDeadline,
    daysRemaining,
    daysOverdue,
    pendingPartnerCount,
    donePartnerCount: donePartners.length,
    completedMemberCount,
    totalMemberCount,
    progress: totalMemberCount ? completedMemberCount / totalMemberCount : 0,
    // An in-progress card always offers a CTA: "Complete Now" while the viewer
    // still has steps left, or "Retake Session" once they are done but partners
    // are not. Overdue styling is layered on top by isOverdue.
    showCta: true,
    ctaIsRetake: iAmDone,
  };
};

export const curriculumSessionMessageKey = state => {
  const {iAmDone, donePartnerCount, pendingPartnerCount} = state;
  if (pendingPartnerCount === 0) {
    return 'sessionAllPartnersCompleted';
  }
  if (iAmDone) {
    return donePartnerCount > 0
      ? 'sessionYouCompletedWaiting'
      : 'sessionPartnersYetToComplete';
  }
  return donePartnerCount > 0
    ? 'sessionYouAndPartnersYetToComplete'
    : 'sessionPleaseComplete';
};

export const orderCurriculumSessions = sessionStatuses =>
  _.sortBy(sessionStatuses, s => _.get(s, 'session.id'));

// `mysessions` returns a status per session the group has begun, while
// `sessions/?not_started` returns bare sessions. Both are shown in one list, so
// the not-started ones are wrapped to match the status shape the cards read.
export const curriculumSessionList = (ongoingSessions, notStartedSessions) => {
  // `mysessions` is parsed with the session spread onto the status object, so
  // the session fields sit at the top level. Nest them back so both sources
  // share one shape.
  const started = _.filter(ongoingSessions, s => _.get(s, 'id')).map(
    ({status, startDate, endDate, completedSteps, partnerCompletedSteps, ...session}) => ({
      session,
      status,
      startDate,
      endDate,
      completedSteps,
      partnerCompletedSteps,
    }),
  );
  const startedIds = new Set(started.map(s => s.session.id));

  const notStarted = _.filter(notStartedSessions, s => s && !startedIds.has(s.id)).map(
    session => ({
      session,
      status: 'NOT_STARTED',
      startDate: null,
      endDate: null,
      completedSteps: [],
      partnerCompletedSteps: [],
    }),
  );

  return orderCurriculumSessions([...started, ...notStarted]);
};

// Whether the group has begun a session, read the same way the cards read it so
// the card and the session page never disagree about offering a preview.
export const isCurriculumSessionStarted = (sessions, sessionId) => {
  const list = curriculumSessionList(
    _.get(sessions, 'ongoingSessions'),
    _.get(sessions, 'allSessions'),
  );
  const match = _.find(list, s => _.get(s, 'session.id') === _.toNumber(sessionId));
  return Boolean(match) && curriculumSessionState(match).card !== sessionCardStates.NOT_STARTED;
};
